import { yupResolver } from "@hookform/resolvers/yup";
import {
  Grid,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import FormWrapper from "src/components/FormWrapper";
import Iconify from "src/components/Iconify";
import { FormProvider, RHFTextField } from "src/components/hook-form";
import RHFAutocomplete from "src/components/hook-form/RHFAutocomplete";
import { restrict } from "src/components/restrict";
import { addUpdateDraftOrder, masterGetData } from "src/pages/api";

import * as Yup from "yup";

const defaultArrayProduct = {
  productID: "",
  isbn: 0,
  quantity: "",
  singleQtyPrice: 0,
  totalPrice: 0,
  publisherID: "",
};

const defaultValues = {
  schoolID: "",
  productDetails: [defaultArrayProduct],
};

const productSchema = Yup.object().shape({
  productID: Yup.object()
    .shape()
    .nullable()
    .typeError("Required")
    .required("Required"),
  publisherID: Yup.object()
    .shape()
    .nullable()
    .typeError("Required")
    .required("Required"),
  // optional: Yup.string().trim().required("Required"),
  quantity: Yup.number()
    .nullable()
    .typeError("Required")
    .required("required")
    .min(1, "Minimum Value should be 1"),
});

const schema = Yup.object().shape({
  productDetails: Yup.array().of(productSchema),
  schoolID: Yup.string().required("Required"),
});
export default function AddDraftOrder({ onClose, row, isEditMode }) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  const [totalPrice, setTotalPrice] = useState(0);
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });
  const { handleSubmit, reset, control, watch, setValue } = methods;

  const { fields, append, remove } = useFieldArray({
    name: "productDetails",
    control,
  });

  const { data } = useQuery({
    queryKey: ["getData", "products", 0, 5000],
    queryFn: masterGetData,
  });
  const { data: publisherList } = useQuery({
    queryKey: ["getData", "publisher", 0, 100],
    queryFn: masterGetData,
  });

  const { data: schoolList } = useQuery({
    queryKey: ["getData", "schools", 0, 100],
    queryFn: masterGetData,
  });

  const mutation = useMutation(
    (req) => addUpdateDraftOrder({ req, isEditMode }),
    {
      onSuccess: () => {
        enqueueSnackbar("Draft created successfully", {
          variant: "success",
        });
        queryClient.invalidateQueries(["draftOrder"]);

        onClose();
      },
      onError: (error) => {
        enqueueSnackbar(error?.message || "Failed to Add", {
          variant: "error",
        });
      },
    }
  );

  const calculatePrice = (index) => {
    const singlePrice = watch(`productDetails.${index}.singleQtyPrice`) || 0;
    const qty = watch(`productDetails.${index}.quantity`) || 0;
    setValue(`productDetails.${index}.totalPrice`, singlePrice * qty || 0);
  };
  const calculatTotal = () => {
    let amt = 0;
    watch("productDetails")?.forEach((el) => {
      if (el.quantity && el.productID) {
        amt += +el.quantity * el.singleQtyPrice;
      }
    });
    setTotalPrice(amt);
  };

  const onSubmit = (data) => {
    const req = {
      ...data,
      productDetails: data.productDetails.map((el) => ({
        publisherID: el.publisherID.id,
        productID: el.productID.id,
        quantity: el.quantity,
      })),
    };
    mutation.mutate(req);
  };

  return (
    <FormWrapper
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      onReset={
        !isEditMode &&
        (() => {
          setTotalPrice(0);
          reset();
        })
      }
      loading={mutation.isLoading}
      maxWidth={"md"}
      fullWidth
      fullScreen
      isEditMode={isEditMode}
      title={isEditMode ? "Update Draft Order" : "Add Draft Order"}
      disabled={watch("products")?.length === 0}
    >
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={3}>
            <RHFTextField
              name={"schoolID"}
              label={"School"}
              size="small"
              select
              SelectProps={{ native: true }}
              fullWidth
              InputLabelProps={{ shrink: true }}
            >
              <option value={""}>Choose a School</option>
              {schoolList &&
                schoolList?.data?.map((el, index) => (
                  <option value={el.id} key={index}>
                    {el.schoolName}
                  </option>
                ))}
            </RHFTextField>
          </Grid>
        </Grid>

        <TableContainer>
          {loading && <LinearProgress />}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={70}>Sr. No.</TableCell>
                <TableCell width={250}>Product</TableCell>
                <TableCell width={250}>Publisher</TableCell>
                <TableCell width={250}>ISBN</TableCell>
                <TableCell width={150}>Qty</TableCell>
                <TableCell width={180}>Single Qty Price</TableCell>
                <TableCell width={150}>Total</TableCell>

                <TableCell>
                  {" "}
                  <IconButton
                    size="small"
                    color="info"
                    title="Add New"
                    onClick={() => {
                      append(defaultArrayProduct);
                    }}
                  >
                    <Iconify icon={"eva:plus-fill"} />
                  </IconButton>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <RHFAutocomplete
                      name={`productDetails.${index}.productID`}
                      native={false}
                      fullWidth
                      size="small"
                      option={data?.data || []}
                      getOptionDisabled={(option) =>
                        watch("productDetails").some(
                          (item) => item?.productID?.id === option.id
                        )
                      }
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      onInputChange={(value) => {
                        setValue(
                          `productDetails.${index}.singleQtyPrice`,
                          value?.price || 0
                        );
                        setValue(
                          `productDetails.${index}.isbn`,
                          value?.isbn || ""
                        );

                        calculatePrice(index);
                        calculatTotal();
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <RHFAutocomplete
                      name={`productDetails.${index}.publisherID`}
                      native={false}
                      fullWidth
                      size="small"
                      option={publisherList?.data || []}
                      getOptionLabel={(option) => option.publisherName}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <RHFTextField
                      name={`productDetails.${index}.isbn`}
                      // label=""
                      placeholder="ISBN"
                      size="small"
                      inputProps={{
                        readOnly: true,
                        style: {
                          backgroundColor: "#f5f5f5",
                          color: "rgba(0, 0, 0, 0.38)", 
                          cursor: "default",
                        },
                      }}
                      onInput={restrict.digits}
                    />
                  </TableCell>
                  <TableCell>
                    <RHFTextField
                      name={`productDetails.${index}.quantity`}
                      label="Qty"
                      size="small"
                      inputProps={{ maxLength: 5 }}
                      onInput={restrict.digits}
                      onInputChange={() => {
                        calculatePrice(index);
                        calculatTotal();
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <RHFTextField
                      name={`productDetails.${index}.singleQtyPrice`}
                      // label=""
                      placeholder="Single Qty Price"
                      size="small"
                      inputProps={{
                        readOnly: true,
                        style: {
                          backgroundColor: "#f5f5f5", 
                          color: "rgba(0, 0, 0, 0.38)", 
                          cursor: "default",
                        },
                      }}
                      onInput={restrict.digits}
                    />
                  </TableCell>
                  <TableCell>
                    <RHFTextField
                      name={`productDetails.${index}.totalPrice`}
                      // label="Total"
                      placeholder="Total"
                      size="small"
                      inputProps={{
                        readOnly: true,
                        style: {
                          backgroundColor: "#f5f5f5",
                          color: "rgba(0, 0, 0, 0.38)", 
                          cursor: "default",
                        },
                      }}
                      onInput={restrict.digits}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="error"
                      title="Delete"
                      onClick={() => {
                        if (!isEditMode) {
                          remove(index);
                          calculatTotal();
                        }
                      }}
                      disabled={index === 0 && !isEditMode}
                    >
                      <Iconify icon={"eva:trash-2-outline"} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TextField
          label="Total Price"
          value={totalPrice}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            readOnly: true,
            style: {
              backgroundColor: "#f5f5f5",
              color: "rgba(0, 0, 0, 0.38)", 
              cursor: "default",
            },
          }}
          size="small"
          sx={{ mt: 2, float: "right" }}
        />
      </FormProvider>
    </FormWrapper>
  );
}
